import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';

import CloseIcon from '@mui/icons-material/Close';

import { selectSelectionMode, selectSelectedCount } from '../redux/features/context/contextSlice';
import { verdictButtonSx } from './common/VerdictBar';
import { BAR_BG, BAR_DIVIDER, BAR_TEXT_MUTED, SANS_STACK } from './common/tokens';

const linkSx = {
  color: BAR_TEXT_MUTED,
  fontSize: '0.85rem',
  cursor: 'pointer',
  padding: '4px 6px',
  borderRadius: '6px',
  whiteSpace: 'nowrap',
  '&:hover': { color: '#ffffff', background: 'rgba(255,255,255,0.08)' },
};

const iconBtnSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'transparent',
  color: '#e0e0e0',
  cursor: 'pointer',
  padding: '8px',
  borderRadius: '50%',
  flexShrink: 0,
};

// ---- Reusable primitives for the entity-specific `actions` slot ----

// A short vertical rule matching the bar's built-in dividers.
export function SelectionBarDivider() {
  return <Box sx={{ width: '1px', height: 24, background: BAR_DIVIDER, mx: '6px' }} />;
}

// Circular icon button. `hoverSx` overrides the default hover (e.g. red for delete).
export function SelectionBarIconButton(props) {
  const { title, onClick, hoverSx, children } = props;
  return (
    <Box
      component="button"
      type="button"
      title={title}
      onClick={onClick}
      sx={{ ...iconBtnSx, '&:hover': hoverSx || { background: 'rgba(255,255,255,0.1)' } }}
    >
      {children}
    </Box>
  );
}

SelectionBarIconButton.propTypes = {
  title: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  hoverSx: PropTypes.object,
  children: PropTypes.node.isRequired,
};

SelectionBarIconButton.defaultProps = { title: '', hoverSx: null };

// Coral pill button — the bar's primary action (e.g. "Edit items").
export function SelectionBarButton(props) {
  const { onClick, children } = props;
  const theme = useTheme();
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        border: 'none',
        color: '#ffffff',
        background: theme.palette.primary.main,
        borderRadius: '999px',
        padding: '8px 18px',
        fontSize: '0.875rem',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.02857em',
        fontFamily: SANS_STACK,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        '&:hover': { background: theme.palette.primary.dark },
      }}
    >
      {children}
    </Box>
  );
}

SelectionBarButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

export function SelectionBarVerdictButton(props) {
  const { kind, onClick, children } = props;
  return (
    <Box component="button" type="button" onClick={onClick} sx={verdictButtonSx(kind)}>
      {children}
    </Box>
  );
}

SelectionBarVerdictButton.propTypes = {
  kind: PropTypes.oneOf(['approve', 'reject']).isRequired,
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

/**
 * Floating pill shown while entities are selected. Entity-agnostic: it renders
 * the count, page/all selection links and a clear button, and delegates the
 * bulk actions to the `actions` slot (built from the primitives above by the
 * owning view — items, lists, authors).
 */
export default function SelectionBar(props) {
  const {
    total, selectAllLabel, onSelectPage, onSelectAll, onClear, actions,
  } = props;

  const selectionMode = useSelector(selectSelectionMode);
  const selectedCount = useSelector(selectSelectedCount);
  // Visible for the whole selection session; actions dim until something is picked.
  if (!selectionMode) return null;
  const hasSelection = selectedCount > 0;

  return (
    <Box
      data-screen-label="Selection bar"
      sx={{
        position: 'fixed',
        bottom: '22px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1250,
        background: BAR_BG,
        borderRadius: '999px',
        boxShadow: '0 12px 32px rgba(0,0,0,0.3), 0 4px 12px rgba(0,0,0,0.15)',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '7px 8px 7px 20px',
        whiteSpace: 'nowrap',
      }}
    >
      <Box component="span" sx={{ color: '#ffffff', fontWeight: 700, fontSize: '0.95rem' }}>
        {`${selectedCount} selected`}
      </Box>
      <Box component="span" onClick={onSelectPage} sx={linkSx}>Select page</Box>
      <Box component="span" onClick={onSelectAll} sx={linkSx}>{selectAllLabel || `Select all (${total})`}</Box>

      {actions && (
        <>
          <SelectionBarDivider />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              opacity: hasSelection ? 1 : 0.4,
              pointerEvents: hasSelection ? 'auto' : 'none',
            }}
          >
            {actions}
          </Box>
        </>
      )}

      <SelectionBarIconButton
        title="Clear selection"
        onClick={onClear}
        hoverSx={{ background: 'rgba(255,255,255,0.1)', color: '#ffffff' }}
      >
        <CloseIcon sx={{ fontSize: 22, color: '#bdbdbd' }} />
      </SelectionBarIconButton>
    </Box>
  );
}

SelectionBar.propTypes = {
  total: PropTypes.number,
  selectAllLabel: PropTypes.string,
  onSelectPage: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  actions: PropTypes.node,
};

SelectionBar.defaultProps = {
  total: 0,
  selectAllLabel: '',
  actions: null,
};
